import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BrandMark, Logo } from "@/components/Logo";
import {
  Gauge,
  ShoppingCart,
  CircleDollarSign,
  Users,
  Package,
  LogOut,
  ClipboardList,
  Siren,
  CarFront,
  Wallet,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";

const NAV = [
  { to: "/", label: "Dashboard", jp: "計器盤", icon: Gauge, roles: ["admin", "mechanic"] },
  { to: "/pos", label: "Point of Sale", jp: "販売", icon: ShoppingCart, roles: ["admin", "mechanic"] },
  { to: "/pd-ems", label: "PD & EMS POS", jp: "警察救急", icon: Siren, roles: ["admin", "mechanic"] },
  { to: "/dealer", label: "Vehicle Dealer", jp: "車両", icon: CarFront, roles: ["admin", "mechanic"] },
  { to: "/sales", label: "Sales Log", jp: "記録", icon: ClipboardList, roles: ["admin", "mechanic"], perm: "can_view_sales" },
  { to: "/commissions", label: "Commissions", jp: "歩合給", icon: CircleDollarSign, roles: ["admin", "mechanic"] },
  { to: "/money", label: "Money", jp: "金庫", icon: Wallet, roles: ["admin"] },
  { to: "/mechanics", label: "Mechanics", jp: "整備士", icon: Users, roles: ["admin"] },
  { to: "/catalog", label: "Catalog", jp: "部品", icon: Package, roles: ["admin"] },
];

function ProtectedShell() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background carbon-bg flex items-center justify-center" data-testid="app-loading">
        <div className="font-display text-primary text-xs tracking-[0.4em] animate-pulse">TOP GEAR</div>
      </div>
    );
  }

  const visible = NAV.filter(
    (n) => n.roles.includes(user.role) && (!n.perm || user.role === "admin" || user[n.perm])
  );

  return (
    <div className="min-h-screen bg-background carbon-bg flex" data-testid="app-shell">
      <aside className="w-16 lg:w-56 shrink-0 border-r border-border bg-[#0C100D] flex flex-col fixed inset-y-0 z-40">
        <div className="px-3 lg:px-5 py-4 border-b border-border hidden sm:block">
          <div className="lg:hidden flex justify-center">
            <Logo size={42} />
          </div>
          <div className="hidden lg:block">
            <BrandMark />
          </div>
        </div>
        <nav className="flex-1 py-4 space-y-1 px-2" data-testid="side-nav">
          {visible.map(({ to, label, jp, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-sm border transition-colors duration-200 ${
                  isActive
                    ? "bg-accent border-primary/40 text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`
              }
            >
              <Icon size={17} strokeWidth={2} />
              <span className="hidden lg:flex flex-1 items-center justify-between">
                <span className="text-xs font-semibold tracking-wide">{label}</span>
                <span className="font-jp text-[9px] opacity-50">{jp}</span>
              </span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="hidden lg:block px-2 pb-3">
            <div className="text-xs font-semibold text-foreground truncate" data-testid="shell-user-name">{user.name}</div>
            <div className="text-[10px] text-primary tracking-widest uppercase" data-testid="shell-user-role">
              {user.role === "admin" ? "Shop Owner" : user.rank || "Mechanic"}
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="w-full flex items-center justify-center lg:justify-start gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-destructive transition-colors"
          >
            <LogOut size={15} />
            <span className="hidden lg:inline">Sign Out</span>
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-16 lg:ml-56 min-w-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="p-4 sm:p-6 lg:p-8"
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}

export default ProtectedShell;
